import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './DependentsForm.css';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import WizardStepper from '../components/ui/WizardStepper';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AxiosError } from 'axios';

type DependentData = {
  type_of_dependents: string;
  dependents_condition: string;
  name: string;
  date_of_birth: string;
  sex: string;
  nationality: string;
};

type DependentItem = DependentData & {
  id_dependents: number;
};

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

const labelMap: Record<keyof DependentData, string> = {
  type_of_dependents: 'Tipo de dependente',
  dependents_condition: 'Condição',
  name: 'Nome',
  date_of_birth: 'Data de nascimento',
  sex: 'Sexo',
  nationality: 'Nacionalidade',
};

const DependentsForm: React.FC = () => {
  const [formData, setFormData] = useState<DependentData>({
    type_of_dependents: '',
    dependents_condition: '',
    name: '',
    date_of_birth: '',
    sex: '',
    nationality: '',
  });

  const [dependentsList, setDependentsList] = useState<DependentItem[]>([]);
  const [dependentId, setDependentId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { id_user, token, logout } = useAuth();

  // Valida se todos os campos do formulário são strings não vazias
  const isFormValid = () => {
    return Object.values(formData).every((value) => typeof value === 'string' && value.trim() !== '');
  };

  // Valida data no formato DD/MM/AAAA
  const isValidDate = (dateStr: string): boolean => {
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!regex.test(dateStr)) return false;

    const [day, month, year] = dateStr.split('/').map(Number);
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > new Date().getFullYear()) return false;

    const date = new Date(year, month - 1, day);
    return date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year;
  };

  useEffect(() => {
    if (!token || !id_user) {
      console.error('Token ou ID do usuário ausente', { token, id_user });
      alert('Sessão expirada. Faça login novamente.');
      navigate('/rj');
      return;
    }
    fetchDependents();
  }, [navigate, token, id_user]);

  const fetchDependents = async () => {
    try {
      const response = await api.get(`/dependents/${id_user}`);
      if (response.data.message === 'Dependents retrieved') {
        const data = Array.isArray(response.data.data)
          ? response.data.data
          : [response.data.data];
        setDependentsList(
          data.map((dep: any) => ({
            id_dependents: dep.id_dependents,
            type_of_dependents: dep.type_of_dependents || '',
            dependents_condition: dep.dependents_condition || '',
            name: dep.name || '',
            date_of_birth: new Date(dep.date_of_birth).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }),
            sex: dep.sex || '',
            nationality: dep.nationality || '',
          }))
        );
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error('Erro ao buscar dependentes:', error.response?.status, error.response?.data);
      setDependentsList([]);
      setErrorMessage(error.response?.data?.message || 'Erro ao carregar dependentes. Tente novamente.');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrorMessage(null);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.length > 2) v = v.slice(0, 2) + '/' + v.slice(2);
    if (v.length > 5) v = v.slice(0, 5) + '/' + v.slice(5, 9);
    setFormData({ ...formData, date_of_birth: v });
    setErrorMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    if (!isFormValid()) {
      setErrorMessage('Por favor, preencha todos os campos obrigatórios antes de adicionar o dependente.');
      setIsLoading(false);
      return;
    }

    if (!isValidDate(formData.date_of_birth)) {
      setErrorMessage('Data de nascimento inválida. Use o formato DD/MM/AAAA e insira uma data válida.');
      setIsLoading(false);
      return;
    }

    if (!token || !id_user) {
      console.error('Token ou ID do usuário ausente durante o envio', { token, id_user });
      alert('Sessão expirada. Faça login novamente.');
      navigate('/rj');
      setIsLoading(false);
      return;
    }

    try {
      const dateParts = formData.date_of_birth.split('/');
      const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

      const payload = {
        type_of_dependents: formData.type_of_dependents,
        dependents_condition: formData.dependents_condition,
        name: formData.name,
        date_of_birth: formattedDate,
        sex: formData.sex,
        nationality: formData.nationality,
      };

      const url = dependentId
        ? `/dependents/by-id/${dependentId}`
        : '/dependents';
      const method = dependentId ? 'put' : 'post';

      await api({ method, url, data: payload });

      setErrorMessage(null);
      setFormData({
        type_of_dependents: '',
        dependents_condition: '',
        name: '',
        date_of_birth: '',
        sex: '',
        nationality: '',
      });
      setDependentId(null);
      fetchDependents();
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error('Erro ao salvar dependente:', error.response?.status, error.response?.data);
      const message = error.response?.data?.message || error.response?.data?.error || 'Erro ao salvar o dependente. Tente novamente.';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este dependente?')) {
      setIsLoading(true);
      try {
        await api.delete(`/dependents/by-id/${id}`);
        fetchDependents();
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        console.error('Erro ao excluir dependente:', error.response?.status, error.response?.data);
        setErrorMessage(error.response?.data?.message || 'Erro ao excluir o dependente.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleEdit = (dep: DependentItem) => {
    // Garante que todos os campos sejam strings
    const sanitizedDep: DependentData = {
      type_of_dependents: dep.type_of_dependents || '',
      dependents_condition: dep.dependents_condition || '',
      name: dep.name || '',
      date_of_birth: dep.date_of_birth || '',
      sex: dep.sex || '',
      nationality: dep.nationality || '',
    };
    setFormData(sanitizedDep);
    setDependentId(dep.id_dependents);
    setErrorMessage(null);
  };

  const handleAddNew = () => {
    setFormData({
      type_of_dependents: '',
      dependents_condition: '',
      name: '',
      date_of_birth: '',
      sex: '',
      nationality: '',
    });
    setDependentId(null);
    setErrorMessage(null);
  };

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="dependents-wrapper">
          <WizardStepper current={3} />
          <div className="dependents-form-container">
            <h2>Formulário de Dependente</h2>
            <AnimatePresence>
              {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
            </AnimatePresence>
        <form className="dependents-form" onSubmit={handleSubmit}>
          {Object.entries(formData).map(([key, value]) => {
            const typedKey = key as keyof DependentData;
            let inputElement;

            if (key === 'type_of_dependents') {
              inputElement = (
                <select
                  id={key}
                  name={key}
                  value={value}
                  onChange={handleChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                >
                  <option value="">Selecione</option>
                  <option value="Filho">Filho</option>
                  <option value="Cônjuge">Cônjuge</option>
                  <option value="Companheiro">Companheiro(a)</option>
                  <option value="Tutelado">Tutelado</option>
                  <option value="Curatelado">Curatelado</option>
                </select>
              );
            } else if (key === 'dependents_condition') {
              inputElement = (
                <select
                  id="dependents_condition"
                  name="dependents_condition"
                  value={value}
                  onChange={handleChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                >
                  <option value="">Selecione</option>
                  <option value="Menor de idade">Menor de idade</option>
                  <option value="Não deficiente">Não deficiente</option>
                  <option value="Deficiente">Deficiente</option>
                </select>
              );
            } else if (key === 'sex') {
              inputElement = (
                <select
                  id="sex"
                  name="sex"
                  value={value}
                  onChange={handleChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                >
                  <option value="">Selecione</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
              );
            } else if (key === 'date_of_birth') {
              inputElement = (
                <input
                  id="date_of_birth"
                  type="text"
                  name="date_of_birth"
                  value={value}
                  onChange={handleDateChange}
                  maxLength={10}
                  placeholder="DD/MM/AAAA"
                  className="form-input"
                  required
                  disabled={isLoading}
                />
              );
            } else {
              inputElement = (
                <input
                  id={key}
                  name={key}
                  value={value}
                  onChange={handleChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                />
              );
            }

            return (
              <div className="form-group" key={key}>
                <label htmlFor={key}>{labelMap[typedKey]}:</label>
                {inputElement}
              </div>
            );
          })}

          <div className="button-group">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/rj/address')}
              disabled={isLoading}
              aria-label="Voltar para o formulário de endereço"
            >
              Anterior
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={!isFormValid() || isLoading}
              aria-label={dependentId ? 'Atualizar dependente' : 'Adicionar dependente'}
            >
              {isLoading
                ? dependentId
                  ? 'Atualizando…'
                  : 'Adicionando…'
                : dependentId
                ? 'Atualizar Dependente'
                : 'Adicionar Dependente'}
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/rj/declarant')}
              disabled={isLoading}
              aria-label="Prosseguir para o próximo formulário"
            >
              Prosseguir
            </Button>
          </div>
        </form>

        <div className="dependents-list-header">
          <h3>Dependentes Cadastrados</h3>
          {!isLoading && (
            <span className="dependents-count">
              {dependentsList.length === 0
                ? 'Nenhum dependente'
                : dependentsList.length === 1
                ? '1 dependente'
                : `${dependentsList.length} dependentes`}
            </span>
          )}
        </div>
        {isLoading ? (
          <p>Carregando dependentes...</p>
        ) : dependentsList.length > 0 ? (
          <table className="dependents-table">
            <thead>
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">Data de Nascimento</th>
                <th scope="col">Sexo</th>
                <th scope="col">Tipo</th>
                <th scope="col">Condição</th>
                <th scope="col">Nacionalidade</th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
                {dependentsList.map((dep) => (
                <motion.tr
                  key={dep.id_dependents}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 12 }}
                  transition={{ duration: 0.2 }}
                >
                  <td>{dep.name}</td>
                  <td>{dep.date_of_birth}</td>
                  <td>{dep.sex === 'M' ? 'Masculino' : 'Feminino'}</td>
                  <td>{dep.type_of_dependents}</td>
                  <td>{dep.dependents_condition}</td>
                  <td>{dep.nationality}</td>
                  <td>
                    <button
                      className="edit-button"
                      onClick={() => handleEdit(dep)}
                      disabled={isLoading}
                      aria-label={`Editar dependente ${dep.name}`}
                    >
                      Editar
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => handleDelete(dep.id_dependents)}
                      disabled={isLoading}
                      aria-label={`Excluir dependente ${dep.name}`}
                    >
                      Excluir
                    </button>
                  </td>
                </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        ) : (
          <p>Nenhum dependente cadastrado ainda.</p>
        )}
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default DependentsForm;